import * as Sentry from '@sentry/vue'
import Vue from 'vue'
import configs from '@/config'
import store from '@/vuex/store'
import { SimpleCache } from '@/utils/cache.utils'
import { mapEssentialPlanNamesToPro } from '@/utils/plan.utils'

let resource = null

export const actions = {
  getUserPlanBenefit: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/plan`,
  },
  // AHA-42358 — entitlements now live on the general-api monolith (the
  // short-lived aha-pricing BFF at /pricing-api/plan-benefit was removed). Same
  // origin + auth as the legacy endpoint above, so it rides the existing Bearer
  // interceptor.
  getUserEntitlements: {
    method: 'GET',
    url: `${configs.baseUrl}/api/pricing/entitlements`,
  },
  getUserLimitationPresentation: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/presentation/{presentationId}/enforce-slidetype`,
  },
  getDataPresentationHandleCopyPlanBenefit: {
    method: 'GET',
    url: `${configs.baseUrl}/api/plan-benefit/presentation/{presentationId}/get-enforce-feature`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

// AHA-42358 — route to the new /api/pricing/entitlements endpoint purely on the
// Unleash flag. The flag is the single kill switch: if anything misbehaves we
// turn it off and every user falls straight back to the legacy
// /api/plan-benefit/plan path. No release-date / createdAt gating — the
// server's free/paid split (free → Stripe mirror, paid → legacy logic) keeps
// grandfathered users safe.
const shouldUseNewEntitlementApi = () => store.getters['featureToggles/canUseNewEntitlementApi']

// The new endpoint is a different service returning the same contract; a 2xx
// with a malformed/partial body would otherwise be written straight into the
// plan-benefit store and silently corrupt entitlements. Require the load-bearing
// `name` field before trusting the response.
const isValidEntitlements = (body) => Boolean(body) && typeof body.name === 'string'

// AHA-42054 — the single chokepoint where the essential plan is scrubbed from
// the free-user upgrade funnel. Remapping the `*_planName` hints here (rather
// than at each CTA) means every downstream consumer — store, getters, popovers —
// sees a response that already points at "pro". Gated on the response's own
// `name === 'free'` so it never depends on store/cookie load order, plus
// `canRemoveEssentialPlan` (derived from the shared pricing lineup — see
// pricingLineup module). A no-op for paid users and when Essential is still shown.
const applyNoEssentialOverride = (body) => {
  const enabled = store.getters['featureToggles/canRemoveEssentialPlan'] && body?.name === 'free'
  return mapEssentialPlanNamesToPro(body, enabled)
}

const getUserPlanBenefit = async (params) => {
  if (shouldUseNewEntitlementApi()) {
    try {
      const res = await resource.getUserEntitlements(params)
      if (!isValidEntitlements(res.body)) {
        throw new Error('entitlements response missing required shape')
      }
      return applyNoEssentialOverride(res.body)
    } catch (err) {
      // Fall through to the legacy endpoint on any error or bad payload. Report
      // it so the rollout signal isn't lost — a silent fallback would hide a
      // misbehaving new endpoint.
      Sentry.captureException(err instanceof Error ? err : new Error('getUserEntitlements failed'), {
        extra: { userId: store.state.currentUser?.id, status: err?.status },
      })
      const fallback = await resource.getUserPlanBenefit(params)
      return applyNoEssentialOverride(fallback.body)
    }
  }
  const res = await resource.getUserPlanBenefit(params)
  return applyNoEssentialOverride(res.body)
}

const getUserLimitationPresentation = async (presentationId, params) => {
  const payload = { ...params, presentationId }
  const res = await resource.getUserLimitationPresentation(payload)
  return res.body
}

const getDataPresentationHandleCopyPlanBenefit = async (presentationId, params) => {
  const payload = { ...params, presentationId }
  const res = await resource.getDataPresentationHandleCopyPlanBenefit(payload)
  return res.body
}

const cache = new SimpleCache()

const getUserPlanBenefitWithCache = async ({ maxAgeInSeconds = 1 }) => {
  return cache.invoke(() => {
    return getUserPlanBenefit()
  }, {
    key: 'getUserPlanBenefit',
    maxAgeInSeconds,
  })
}

export default {
  initResource,
  getUserPlanBenefit,
  getUserPlanBenefitWithCache,
  getUserLimitationPresentation,
  getDataPresentationHandleCopyPlanBenefit,
}
